import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createCampaignType,
  getAllCampaignTypes,
  getCampaignTypeById,
  updateCampaignType,
  deleteCampaignType,
  createStatus,
  getAllStatuses,
  getStatusById,
  updateStatus,
  deleteStatus,
  createPricingModel,
  getAllPricingModels,
  getPricingModelById,
  updatePricingModel,
  deletePricingModel,
  createCampaign,
  getCampaigns,
  getCampaignById,
  updateCampaign,
  deleteCampaign,
  getVouchers,
  createVoucher,
  getVoucherById,
  getActiveVouchers,
  updateVoucher,
  deleteVoucher,
  getVouchersByCampaign,
  downloadCouponTemplate,
  createCoupon,
  getCoupons,
  getCouponLogs,
  getUserCoupons,
  getCampaignImpact,
  uploadPlantationCertificate
} from "../api/campaign";

// Campaign Types Hooks
export const useCreateCampaignType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createCampaignType(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["campaignTypes"]);
    },
  });
};

export const useGetCampaignTypes = () => {
  return useQuery({
    queryKey: ["campaignTypes"],
    queryFn: () => getAllCampaignTypes(),
    refetchOnWindowFocus: false,
  });
};

export const useGetCampaignTypeById = (id) => {
  return useQuery({
    queryKey: ["campaignType", id],
    queryFn: () => getCampaignTypeById(id),
    enabled: !!id,
  });
};

export const useUpdateCampaignType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateCampaignType(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["campaignTypes"]);
    },
  });
};

export const useDeleteCampaignType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteCampaignType(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["campaignTypes"]);
    },
  });
};

// Campaign Status Hooks
export const useCreateStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["statuses"]);
    },
  });
};

export const useGetStatuses = () => {
  return useQuery({
    queryKey: ["statuses"],
    queryFn: () => getAllStatuses(),
    refetchOnWindowFocus: false,
  });
};

export const useGetStatusById = (id) => {
  return useQuery({
    queryKey: ["status", id],
    queryFn: () => getStatusById(id),
    enabled: !!id,
  });
};

export const useUpdateStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateStatus(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["statuses"]);
    },
  });
};

export const useDeleteStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["statuses"]);
    },
  });
};

// Pricing Model Hooks
export const useCreatePricingModel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createPricingModel(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["pricingModels"]);
    },
  });
};

export const useGetPricingModels = () => {
  return useQuery({
    queryKey: ["pricingModels"],
    queryFn: () => getAllPricingModels(),
    refetchOnWindowFocus: false,
  });
};

export const useGetPricingModelById = (id) => {
  return useQuery({
    queryKey: ["pricingModel", id],
    queryFn: () => getPricingModelById(id),
    enabled: !!id,
  });
};

export const useUpdatePricingModel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updatePricingModel(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["pricingModels"]);
    },
  });
};

export const useDeletePricingModel = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deletePricingModel(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["pricingModels"]);
    },
  });
};

// Campaign Hooks
export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["campaigns"]);
    },
  });
};

export const useGetCampaigns = () => {
  return useQuery({
    queryKey: ["campaigns"],
    queryFn: () => getCampaigns(),
    refetchOnWindowFocus: false,
  });
};

export const useGetCampaignById = (id) => {
  return useQuery({
    queryKey: ["campaign", id],
    queryFn: () => getCampaignById(id),
    enabled: !!id,
  });
};

export const useUpdateCampaign = (id) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(["campaigns"]);
    },
  });
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["campaigns"]);
    },
  });
};

// Voucher Hooks
export const useGetVoucherByCampaign = (campaignId) => {
  return useQuery({
    queryKey: ["vouchers", campaignId],
    queryFn: () => downloadCouponTemplate(campaignId),
    enabled: !!campaignId,
    refetchOnWindowFocus: false,
  });
}

export const useCreateCoupon = () => {
  return useMutation({
    mutationKey: ['create-coupon'],
    mutationFn: (data) => createCoupon(data),
  });
}

export const useGetCoupons = () => {
  return useQuery({
    queryKey: ["coupons"],
    queryFn: () => getCoupons(),
  });
}

export const useGetCouponLogs = (couponId, couponCode, enabled = true) => {
  return useQuery({
    queryKey: ["couponLogs", couponId],
    queryFn: () => getCouponLogs(couponId, couponCode),
    enabled: enabled && !!couponId, // Only run query when enabled and couponId exists
    staleTime: 0, // Always refetch when component mounts
    cacheTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

export const useGetUserCoupons = () => {
  return useQuery({
    queryKey: ["userCoupons"],
    queryFn: () => getUserCoupons(),
  });
}

export const useCreateVoucher = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => createVoucher(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["vouchers"]);
    },
  });
}

export const useGetVouchers = () => {
  return useQuery({
    queryKey: ["vouchers"],
    queryFn: () => getVouchers(),
  });
}

export const useGetActiveVouchers = () => {
  return useQuery({
    queryKey: ["activeVouchers"],
    queryFn: () => getActiveVouchers(),
    refetchOnWindowFocus: false,
  });
}

export const useGetVoucherById = (id) => {
  return useQuery({
    queryKey: ["voucher", id],
    queryFn: () => getVoucherById(id),
    enabled: !!id,
  });
}

export const useUpdateVoucher = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => updateVoucher(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["vouchers"]);
    },
  });
}

export const useDeleteVoucher = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteVoucher(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["vouchers"]);
    },
  });
}

export const useDeleteCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteVoucher(id),
    onSuccess: () => {
      queryClient.invalidateQueries(["coupons"]);
    },
  });
}

// campaign impact
export const useGetCampaignImpact = (id) => {
  return useQuery({
    queryKey: ["campaignImpact", id],
    queryFn: () => getCampaignImpact(id),
    enabled: !!id,
  });
}

export const useUploadPlantationCertificate = () => {
  return useMutation({
    mutationFn: ({ userId, campaignId, certificate }) => uploadPlantationCertificate({ userId, campaignId, certificate }),
  });
};